import axios from 'axios';
import queryString, { StringifiableRecord } from 'query-string-for-all';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import {
  CACHE_KEYS,
  CampaignCost,
  Project,
  RecurringProjectNotification,
} from '.';
import { api } from './swaggerApi';

const PREFIX_URL = '/insights_platform/projects';

export interface ShowProjectResponse {
  data: Project;
}

export interface ProjectsPagination {
  total_page: number;
  current_page: number;
  limit_value: number;
  next_page: number;
  prev_page: number;
  total_count: number;
}
export interface ProjectsQuery {
  query?: string;
  status?: string;
  user_id?: string[];
  page?: number;
  skip_pagination?: string;
  project_type?: string;
  managed?: string;
  sort_by?: string[];
}
export interface ProjectValidation {
  valid_project: boolean;
  valid_campaign: boolean;
  campaign_cost: CampaignCost;
  campaign_errors: Record<string, Record<string, unknown>[]>;
  notifications: RecurringProjectNotification[];
}

export const list = async (query: ProjectsQuery) =>
  await axios.get<{ projects: Project[]; pagination: ProjectsPagination }>(
    queryString.stringifyUrl(
      {
        url: PREFIX_URL,
        query: query as StringifiableRecord,
      },
      { arrayFormat: 'bracket' }
    )
  );

export const update = async (identifier: string, payload: Partial<Project>) => {
  const { data } = await axios.put(`${PREFIX_URL}/${identifier}`, {
    ...payload,
    identifier,
  });
  return data;
};

export const clone = async (identifier: string, name: string) => {
  const { data } = await axios.post(`${PREFIX_URL}/${identifier}/clone`, {
    name,
  });
  return data;
};

const validations = async (projectIdentifier: string) => {
  const url = `${PREFIX_URL}/${projectIdentifier}/validations`;
  return await axios.get<ProjectValidation>(url);
};

export const updateStatus = ({
  identifier,
  status,
}: {
  identifier: string;
  status: number;
}) => axios.post(`${PREFIX_URL}/${identifier}/update_status`, { status });

export const create = async (
  name: string,
  project_type: number,
  is_managed: number,
  campaignAttributes: unknown
) => {
  const { data } = await axios.post<ShowProjectResponse>(`${PREFIX_URL}`, {
    name,
    project_type,
    is_managed,
    campaigns_attributes: campaignAttributes,
  });
  return data;
};

export const useGetProjects = (...args: Parameters<typeof list>) => {
  const searchQuery = args[0];
  return useQuery([CACHE_KEYS.PROJECTS, searchQuery], () => list(searchQuery), {
    keepPreviousData: true,
  });
};

export const useGetProject = (identifier: string) => {
  const queryClient = useQueryClient();

  return useQuery(
    [CACHE_KEYS.PROJECT, identifier],
    async () => {
      const { data } = await api.insightsPlatform.projectsDetail(identifier);
      return data;
    },
    {
      // if the project is present in the projects cache, returns it inmediately
      // while performing the query in background to retrieve all the missing data
      placeholderData: () => {
        const projectsCache: Project[] = queryClient
          .getQueriesData(CACHE_KEYS.PROJECTS)
          .reduce((acc, [, cache]) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return [...acc, ...(cache as any).data.projects];
          }, []);

        const project = projectsCache?.find(
          (project) => project.id === identifier
        );

        return { data: { ...project } };
      },
    }
  );
};

export const useUpdateProjectStatus = (identifier: string) => {
  const queryClient = useQueryClient();
  const mutation = useMutation(
    (status: number) => updateStatus({ identifier, status }),
    {
      onSuccess: (data, status) => {
        queryClient.setQueriesData(
          CACHE_KEYS.PROJECTS,
          (cache: { data: { projects: Project[] } }) => {
            const updatedProjects = cache.data.projects.map((project) => {
              if (project.id === identifier) {
                project.attributes.status = status;
                return project;
              }
              return project;
            });
            return { data: { projects: updatedProjects } };
          }
        );
      },
    }
  );

  return mutation;
};

export const useUpdateProject = (identifier: string) => {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (newData: Partial<Project>) => update(identifier, newData),
    {
      onSuccess: (response) => {
        queryClient.setQueriesData(
          [CACHE_KEYS.PROJECT, identifier],
          () => response
        );
        queryClient.setQueriesData(
          CACHE_KEYS.PROJECTS,
          (cache: { data: { projects: Project[] } }) => {
            const updatedProjects = cache.data.projects.map((project) => {
              if (project.id === identifier) {
                const { data } = response;
                return { ...data };
              }
              return project;
            });

            return { data: { projects: updatedProjects } };
          }
        );
      },
    }
  );

  return mutation;
};

export const useCreateProject = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (args: Parameters<typeof create>) => {
      return create(...args);
    },
    {
      onSuccess: (data) => {
        queryClient.setQueryData([CACHE_KEYS.PROJECT, data.data.id], data);
        queryClient.invalidateQueries([CACHE_KEYS.PROJECTS]);
      },
    }
  );
};

export const useCloneProject = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (args: Parameters<typeof clone>) => {
      return clone(...args);
    },
    {
      onSuccess: (data) => {
        queryClient.setQueryData([CACHE_KEYS.PROJECT, data.data.id], data);
        queryClient.invalidateQueries([CACHE_KEYS.PROJECTS]);
      },
    }
  );
};

interface CampaignErrors {
  questions: Record<string, unknown>;
  total_remaining?: string[];
  only_messages?: string[];
  no_questions?: string[];
  status?: string[];
  base?: string[];
}

export const useProjectValidation = (projectIdentifier: string) => {
  return useQuery(
    [CACHE_KEYS.PROJECT_VALIDATION, projectIdentifier],
    async () => {
      const { data } = await validations(projectIdentifier);
      const {
        valid_project: isProjectValid,
        valid_campaign: isCampaignValid,
        notifications,
        campaign_errors = {},
        campaign_cost,
      } = data;
      const { questions_errors = [], ...rest } = campaign_errors;
      const { amount_due: amountDue, total: totalCost } = campaign_cost || {};
      return {
        isProjectValid,
        isCampaignValid,
        amountDue,
        totalCost,
        notifications,
        errors: {
          ...rest,
          questions: extractErrors(questions_errors),
        } as CampaignErrors,
      };
    },
    {
      enabled: !!projectIdentifier,
    }
  );
};

const extractErrors = (data: Record<string, unknown>[]) => {
  return (
    data.reduce((acc, item) => {
      return { ...acc, ...item };
    }, {}) || {}
  );
};

export const useGetProjectStatusesCount = (
  ...args: [string, string, string, string[]] // Parameters<typeof api.insightsPlatform.projectStatusCountsList>
) => {
  return useQuery(
    [CACHE_KEYS.PROJECT_STATUSES_COUNT, args],
    async () => {
      // TODO: api generation doesn't work for get requests with params, we can fix this by writing our own axios template
      const urlWithQueryParams = queryString.stringifyUrl(
        {
          url: '/insights_platform/project_status_counts',
          query: {
            managed: args[0],
            project_type: args[1],
            date: args[2],
            user_id: args[3],
          } as StringifiableRecord,
        },
        { arrayFormat: 'bracket' }
      );

      return await api.insightsPlatform.projectStatusCountsList('', '', '', {
        path: urlWithQueryParams,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);
    },
    {
      keepPreviousData: true,
    }
  );
};
